import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Edit3, Factory, Search, AlertTriangle, ShieldCheck, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import ExportButton from "@/components/ExportButton";
import { api } from "@/lib/api";
import axios from "axios";
import { API } from "@/lib/api";
import { formatDateEU, todayISO } from "@/lib/constants";

const STATUS_COLORS = {
  Completed: "#1F4D3A",
  "In Production": "#E85D04",
  "Planned Production": "#F4A261",
  Recalled: "#D62828",
  Archived: "#6C757D",
};

const blank = () => ({
  batch_id: "",                     // backend fills if empty
  product: "Salsa Verde Habanero",
  production_date: todayISO(),
  liters_total: 0,
  liters_b2b: 0,
  liters_b2c: 0,
  ph: 3.6,
  production_notes: "",
  production_status: "Completed",
  created_by: "",
  pasteurization_ok: null,
  operator: "",
  storage: "",
  shelf_life_months: 3,
});

// Live unit conversion
const computeUnits = (liters_b2b, liters_b2c) => ({
  units_b2b: Math.floor((Number(liters_b2b) || 0) / 2.5),
  units_b2c: Math.floor((Number(liters_b2c) || 0) / 0.25),
});

export default function ProductionBatches() {
  const [batches, setBatches] = useState([]);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // Recall modal
  const [recallTarget, setRecallTarget] = useState(null);
  const [recallReason, setRecallReason] = useState("");
  const [recallResult, setRecallResult] = useState(null);
  const [recalling, setRecalling] = useState(false);
  // Label modal
  const [labelTarget, setLabelTarget] = useState(null);
  const [labelCount, setLabelCount] = useState(10);

  const load = async () => {
    const [b, m] = await Promise.all([api.listProductionBatches(), api.getMeta()]);
    setBatches(b);
    setMeta(m);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return batches.filter((b) => {
      const s = search.toLowerCase();
      const matches = !s
        || b.batch_id?.toLowerCase().includes(s)
        || b.product?.toLowerCase().includes(s)
        || b.production_notes?.toLowerCase().includes(s);
      const status = statusFilter === "all" || b.production_status === statusFilter;
      return matches && status;
    });
  }, [batches, search, statusFilter]);

  const fetchNextBatchId = async (product, date) => {
    try {
      const tok = typeof window !== "undefined" ? window.localStorage.getItem("garnacha_token") : null;
      const headers = tok ? { Authorization: `Bearer ${tok}` } : {};
      const r = await axios.get(`${API}/production-batches/next-batch-id`, {
        params: { product, date },
        withCredentials: false,
        headers,
      });
      return r.data?.batch_id || "";
    } catch {
      return "";
    }
  };

  const openCreate = async () => {
    const f = blank();
    f.batch_id = await fetchNextBatchId(f.product, f.production_date);
    setEditing(null);
    setForm(f);
    setOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({ ...blank(), ...b });
    setOpen(true);
  };

  const onProductChange = async (v) => {
    const next = !editing ? await fetchNextBatchId(v, form.production_date) : form.batch_id;
    setForm((f) => ({ ...f, product: v, batch_id: next }));
  };

  const onDateChange = async (v) => {
    const next = !editing ? await fetchNextBatchId(form.product, v) : form.batch_id;
    setForm((f) => ({ ...f, production_date: v, batch_id: next }));
  };

  const totalSplit = (Number(form.liters_b2b) || 0) + (Number(form.liters_b2c) || 0);
  const splitMatches = Math.abs(totalSplit - (Number(form.liters_total) || 0)) < 0.01;
  const { units_b2b, units_b2c } = computeUnits(form.liters_b2b, form.liters_b2c);

  const save = async () => {
    if (!form.product) return toast.error("Pick a product");
    if (!form.production_date) return toast.error("Pick a production date");
    const total = Number(form.liters_total) || 0;
    const b2b = Number(form.liters_b2b) || 0;
    const b2c = Number(form.liters_b2c) || 0;
    if (total <= 0) return toast.error("Total liters produced must be > 0");
    if (Math.abs(b2b + b2c - total) > 0.01) {
      return toast.error(`B2B + B2C (${b2b + b2c}L) must equal Total (${total}L)`);
    }
    try {
      if (editing) {
        await api.updateProductionBatch(editing.id, form);
        toast.success("Batch updated · stock adjusted");
      } else {
        await api.createProductionBatch(form);
        toast.success(`Batch created · stock +${units_b2b} containers / +${units_b2c} bottles`);
      }
      setOpen(false);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to save batch";
      toast.error(msg);
    }
  };

  const del = async (b) => {
    if (!window.confirm(
      `Delete batch "${b.batch_id}"?\n\nThis will roll back ${b.units_b2b || 0} containers and ${b.units_b2c || 0} bottles from stock.`
    )) return;
    try {
      await api.deleteProductionBatch(b.id);
      toast.success("Batch deleted · stock rolled back");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const openRecall = (b) => {
    setRecallTarget(b);
    setRecallReason("");
    setRecallResult(null);
  };

  const confirmRecall = async () => {
    if (!recallTarget) return;
    setRecalling(true);
    try {
      const r = await api.recallProductionBatch(recallTarget.id, recallReason || "");
      setRecallResult(r);
      toast.success(`Batch recalled · ${r.affected_orders} orders, ${r.affected_customers} clients affected`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to recall");
    } finally {
      setRecalling(false);
    }
  };

  // Compute Best Before from production_date + shelf_life_months
  const bestBeforeFor = (b) => {
    const months = b.shelf_life_months || 3;
    const src = b.production_date || todayISO();
    const d = new Date(src + "T00:00:00");
    d.setMonth(d.getMonth() + months);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    return `${dd}.${mm}.${yy}`;
  };

  const printLabels = () => {
    if (!labelTarget) return;
    const bb = bestBeforeFor(labelTarget);
    const lot = labelTarget.batch_id;
    const count = Math.max(1, Math.min(200, Number(labelCount) || 1));
    // Public backend endpoint — no auth needed, so the popup window can render it inline.
    const logoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/assets/garnacha-logo-round.png`;
    // Build one page per label with @page 54x25mm
    const labelHTML = Array.from({ length: count }).map(() => `
      <div class="label">
        <img src="${logoUrl}" class="logo" alt="Garnacha" crossorigin="anonymous"/>
        <div class="info">
          <div class="line1">LOT: ${lot}</div>
          <div class="line2">BB: ${bb}</div>
        </div>
      </div>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Garnacha Labels</title>
      <style>
        @page { size: 54mm 25mm; margin: 0; }
        html, body { margin: 0; padding: 0; background: #fff; font-family: 'Helvetica Neue', Arial, sans-serif; }
        .label {
          width: 54mm; height: 25mm; box-sizing: border-box;
          display: flex; flex-direction: row; align-items: center;
          padding: 1.5mm 2mm; page-break-after: always; overflow: hidden; gap: 2mm;
        }
        .label:last-child { page-break-after: auto; }
        .logo {
          height: 21mm; width: 21mm; object-fit: contain; flex-shrink: 0;
        }
        .info {
          flex: 1; display: flex; flex-direction: column; justify-content: center; align-items: center;
        }
        .line1 {
          font-weight: 800; font-size: 10pt; letter-spacing: 0.4pt;
          font-family: 'Courier New', monospace; text-align: center; line-height: 1.15;
        }
        .line2 {
          font-weight: 700; font-size: 11pt; margin-top: 1mm;
          font-family: 'Courier New', monospace; text-align: center;
        }
        @media screen {
          body { background: #ddd; padding: 12px; }
          .label { border: 1px dashed #888; background: #fff; margin-bottom: 6px; }
        }
      </style></head><body>${labelHTML}
      <script>
        // Wait for logo images to load before triggering print (prevents blank labels).
        const imgs = Array.from(document.images);
        Promise.all(imgs.map(i => i.complete ? Promise.resolve() :
          new Promise(res => { i.onload = res; i.onerror = res; })))
          .then(() => setTimeout(() => window.print(), 200));
      </script>
      </body></html>`;
    const w = window.open("", "_blank", "width=560,height=320");
    if (!w) return toast.error("Popup blocked — allow popups to print");
    w.document.write(html);
    w.document.close();
    toast.success(`${count} label${count > 1 ? "s" : ""} sent to printer`);
  };

  if (!meta) return <div className="text-sm text-[#6C757D]">Loading…</div>;

  return (
    <div data-testid="page-production">
      <PageHeader
        overline="Food Traceability"
        title="Production Batches"
        subtitle="Track every litre. B2B (2.5 L) and B2C (250 ml) units flow straight into stock."
        actions={
          <div className="flex gap-2 flex-wrap">
            <ExportButton module="production-batches" label="Export CSV" />
            <Button
              onClick={openCreate}
              data-testid="add-batch-btn"
              className="bg-[#E85D04] hover:bg-[#c94b03] text-white"
            >
              <Plus size={16} className="mr-2" /> New Batch
            </Button>
          </div>
        }
      />

      <div className="card-soft p-4 mb-5 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search batch ID, product or notes"
            className="pl-9 bg-white border-[#E5E5E5]"
            data-testid="batch-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-56 bg-white border-[#E5E5E5]" data-testid="batch-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {meta.production_statuses.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={Factory}
          title="No production batches yet"
          description="Log a production batch to feed stock automatically."
          action={
            <Button onClick={openCreate} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="empty-add-batch">
              <Plus size={16} className="mr-2" /> Log Batch
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((b) => {
            // Backward-compat: old batches may not have units_b2b/_b2c set.
            const u_b2b = b.units_b2b ?? 0;
            const u_b2c = b.units_b2c ?? 0;
            const totalL = b.liters_total ?? (b.allocated_volume_liters || b.liters_produced || 0);
            return (
              <div key={b.id} className="card-soft card-soft-hover p-5 flex flex-col gap-3" data-testid={`batch-card-${b.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#6C757D] font-bold">
                      {b.product}
                    </div>
                    <h3 className="font-display font-bold text-lg text-[#E85D04] font-mono tracking-tight">
                      {b.batch_id}
                    </h3>
                    <div className="text-xs text-[#6C757D]">Produced {formatDateEU(b.production_date)}</div>
                  </div>
                  <span
                    className="px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider shrink-0"
                    style={{
                      backgroundColor: `${STATUS_COLORS[b.production_status] || "#6C757D"}1f`,
                      color: STATUS_COLORS[b.production_status] || "#6C757D",
                    }}
                    data-testid={`batch-status-${b.id}`}
                  >
                    {b.production_status}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="col-span-3 bg-[#F7F1E8] rounded-lg p-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-[#6C757D] font-bold">Total Produced</div>
                    <div className="font-display font-bold text-xl text-[#1F4D3A]">{totalL} L</div>
                  </div>
                  <div className="bg-[#E85D04]/10 rounded-lg p-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-[#E85D04] font-bold">B2B · 2.5 L</div>
                    <div className="font-semibold text-[#222]">{b.liters_b2b || 0} L</div>
                    <div className="text-[11px] text-[#6C757D] font-mono">→ {u_b2b} containers</div>
                  </div>
                  <div className="bg-[#2A9D8F]/10 rounded-lg p-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-[#2A9D8F] font-bold">B2C · 250 ml</div>
                    <div className="font-semibold text-[#222]">{b.liters_b2c || 0} L</div>
                    <div className="text-[11px] text-[#6C757D] font-mono">→ {u_b2c} bottles</div>
                  </div>
                  <div className="bg-white rounded-lg p-2.5 border border-[#E5E5E5]">
                    <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">pH</div>
                    <div className="font-semibold">{b.ph ?? "—"}</div>
                  </div>
                </div>
                {b.production_notes && (
                  <p className="text-sm text-[#222]/80 line-clamp-2">{b.production_notes}</p>
                )}
                <div className="flex justify-between items-center border-t border-[#E5E5E5] pt-3">
                  <div className="text-[11px] text-[#6C757D]">
                    By {b.created_by || "—"}
                    {b.operator ? ` · Op: ${b.operator}` : ""}
                    {b.storage ? ` · ${b.storage}` : ""}
                    {b.pasteurization_ok === true && <span className="ml-1 text-[#1F4D3A]">· Pasteur ✓</span>}
                    {b.pasteurization_ok === false && <span className="ml-1 text-[#D62828]">· Pasteur ✗</span>}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => { setLabelTarget(b); setLabelCount(10); }}
                      className="text-[#1F4D3A] hover:bg-[#1F4D3A]/10"
                      data-testid={`print-label-${b.id}`}
                      title="Print DYMO labels (54x25mm)"
                    >
                      <Printer size={16} className="mr-1" /> Label
                    </Button>
                    {b.production_status !== "Recalled" && (
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => openRecall(b)}
                        className="text-[#D62828] hover:bg-[#D62828]/10"
                        data-testid={`recall-batch-${b.id}`}
                        title="Mark batch as recalled"
                      >
                        <AlertTriangle size={16} className="mr-1" /> Recall
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => openEdit(b)} data-testid={`edit-batch-${b.id}`}>
                      <Edit3 size={16} className="mr-1" /> Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => del(b)} className="text-[#E76F51] hover:bg-[#E76F51]/10" data-testid={`delete-batch-${b.id}`}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-xl" data-testid="batch-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editing ? "Edit Production Batch" : "New Production Batch"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Product *" className="col-span-2">
              <Select value={form.product} onValueChange={onProductChange}>
                <SelectTrigger data-testid="input-batch-product"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.batchable_products.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Production Date">
              <Input type="date" value={form.production_date || ""}
                onChange={(e) => onDateChange(e.target.value)} data-testid="input-batch-date" />
            </Field>
            <Field label="Batch ID (auto)">
              <Input
                value={form.batch_id}
                readOnly
                className="font-mono bg-[#F7F1E8] text-[#E85D04] font-bold"
                data-testid="input-batch-id"
              />
            </Field>
            <Field label="Total Liters Produced *" className="col-span-2">
              <Input
                type="number" min="0" step="0.5"
                value={form.liters_total === 0 ? "" : form.liters_total}
                onChange={(e) => setForm({ ...form, liters_total: e.target.value === "" ? "" : Number(e.target.value) })}
                placeholder="e.g. 20"
                data-testid="input-liters-total"
              />
            </Field>
            <Field label="B2B Liters (2.5 L Horeca containers)">
              <Input
                type="number" min="0" step="0.5"
                value={form.liters_b2b === 0 ? "" : form.liters_b2b}
                onChange={(e) => setForm({ ...form, liters_b2b: e.target.value === "" ? "" : Number(e.target.value) })}
                placeholder="e.g. 5"
                data-testid="input-liters-b2b"
              />
            </Field>
            <Field label="B2C Liters (250 ml Retail bottles)">
              <Input
                type="number" min="0" step="0.5"
                value={form.liters_b2c === 0 ? "" : form.liters_b2c}
                onChange={(e) => setForm({ ...form, liters_b2c: e.target.value === "" ? "" : Number(e.target.value) })}
                placeholder="e.g. 15"
                data-testid="input-liters-b2c"
              />
            </Field>
            <div
              className={`col-span-2 rounded-lg p-3 border-2 ${splitMatches && Number(form.liters_total) > 0 ? "border-[#1F4D3A]/30 bg-[#1F4D3A]/5" : "border-[#E76F51]/40 bg-[#E76F51]/5"}`}
              data-testid="units-preview"
            >
              <div className="text-[10px] uppercase tracking-wider text-[#6C757D] font-bold mb-1">
                Stock Preview
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-[#E85D04] font-bold">B2B</span>{" "}
                  → <span className="font-display font-bold">{units_b2b}</span> containers (2.5 L)
                </div>
                <div>
                  <span className="text-[#2A9D8F] font-bold">B2C</span>{" "}
                  → <span className="font-display font-bold">{units_b2c}</span> bottles (250 ml)
                </div>
              </div>
              {!splitMatches && Number(form.liters_total) > 0 && (
                <div className="text-[#E76F51] text-xs mt-2 font-semibold" data-testid="split-error">
                  ⚠ B2B ({form.liters_b2b}L) + B2C ({form.liters_b2c}L) = {totalSplit}L · doesn{"'"}t match Total ({form.liters_total}L)
                </div>
              )}
            </div>
            <Field label="pH">
              <Input type="number" step="0.01" value={form.ph ?? ""}
                onChange={(e) => setForm({ ...form, ph: e.target.value === "" ? null : Number(e.target.value) })}
                data-testid="input-batch-ph" />
            </Field>
            <Field label="Production Status">
              <Select value={form.production_status} onValueChange={(v) => setForm({ ...form, production_status: v })}>
                <SelectTrigger data-testid="input-batch-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.production_statuses.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Created By" className="col-span-2">
              <Input value={form.created_by} onChange={(e) => setForm({ ...form, created_by: e.target.value })}
                placeholder="Name of operator / team" data-testid="input-batch-created-by" />
            </Field>
            <Field label="Production Notes" className="col-span-2">
              <Textarea rows={3} value={form.production_notes}
                onChange={(e) => setForm({ ...form, production_notes: e.target.value })}
                data-testid="input-batch-notes" />
            </Field>
            {/* ---- Omavalvonta (Self-Monitoring) ---- */}
            <div className="col-span-2 pt-2 border-t border-[#E5E5E5]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#1F4D3A] font-bold mb-2 flex items-center gap-2">
                <ShieldCheck size={14} /> Omavalvonta (Self-Monitoring)
              </div>
            </div>
            <Field label="Pasteurization OK?">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={form.pasteurization_ok === true ? "default" : "outline"}
                  className={form.pasteurization_ok === true ? "bg-[#1F4D3A] hover:bg-[#173629] text-white" : ""}
                  onClick={() => setForm({ ...form, pasteurization_ok: true })}
                  data-testid="pasteur-yes"
                >
                  Yes
                </Button>
                <Button
                  type="button"
                  variant={form.pasteurization_ok === false ? "default" : "outline"}
                  className={form.pasteurization_ok === false ? "bg-[#D62828] hover:bg-[#a51e1e] text-white" : ""}
                  onClick={() => setForm({ ...form, pasteurization_ok: false })}
                  data-testid="pasteur-no"
                >
                  No
                </Button>
              </div>
            </Field>
            <Field label="Storage">
              <Select value={form.storage || "none"} onValueChange={(v) => setForm({ ...form, storage: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="input-storage"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  <SelectItem value="Room Temp">Room Temp</SelectItem>
                  <SelectItem value="Fridge">Fridge</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Shelf Life">
              <Select value={String(form.shelf_life_months || 3)} onValueChange={(v) => setForm({ ...form, shelf_life_months: Number(v) })}>
                <SelectTrigger data-testid="input-shelf-life"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Month</SelectItem>
                  <SelectItem value="2">2 Months</SelectItem>
                  <SelectItem value="3">3 Months</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Operator" className="col-span-2">
              <Input value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })}
                placeholder="Who ran the batch" data-testid="input-operator" />
            </Field>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cancel-batch">Cancel</Button>
            <Button
              onClick={save}
              disabled={!splitMatches || Number(form.liters_total) <= 0}
              className="bg-[#E85D04] hover:bg-[#c94b03] text-white disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="save-batch"
            >
              {editing ? "Save Changes" : "Create Batch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recall Modal */}
      <Dialog open={!!recallTarget} onOpenChange={(o) => { if (!o) { setRecallTarget(null); setRecallResult(null); }}}>
        <DialogContent className="bg-white max-w-lg" data-testid="recall-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#D62828] flex items-center gap-2">
              <AlertTriangle size={20} /> Recall Batch
            </DialogTitle>
          </DialogHeader>

          {!recallResult ? (
            <div className="space-y-3">
              <div className="bg-[#D62828]/10 border border-[#D62828]/30 rounded-lg p-3 text-sm">
                <div className="font-bold text-[#222]">{recallTarget?.product}</div>
                <div className="font-mono text-[#D62828] font-bold">{recallTarget?.batch_id}</div>
                <div className="text-xs text-[#6C757D] mt-1">
                  Produced {formatDateEU(recallTarget?.production_date)} · {recallTarget?.units_b2b || 0} containers + {recallTarget?.units_b2c || 0} bottles
                </div>
              </div>
              <p className="text-sm text-[#222]/80">
                All orders containing this product placed on/after the batch date will be tagged as <strong>RECALLED</strong>.
                The batch status will switch to <strong>Recalled</strong>.
              </p>
              <Field label="Reason (optional)">
                <Textarea
                  rows={2}
                  value={recallReason}
                  onChange={(e) => setRecallReason(e.target.value)}
                  placeholder="e.g. Contamination detected during routine check"
                  data-testid="recall-reason"
                />
              </Field>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRecallTarget(null)} data-testid="recall-cancel">
                  Cancel
                </Button>
                <Button
                  onClick={confirmRecall}
                  disabled={recalling}
                  className="bg-[#D62828] hover:bg-[#a51e1e] text-white disabled:opacity-50"
                  data-testid="recall-confirm"
                >
                  {recalling ? "Recalling…" : "Confirm Recall"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3" data-testid="recall-result">
              <div className="bg-[#1F4D3A]/10 border border-[#1F4D3A]/30 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-[#1F4D3A] font-bold mb-1">Recall Complete</div>
                <div className="font-display font-bold text-2xl text-[#222]">
                  {recallResult.affected_orders} orders · {recallResult.affected_customers} clients
                </div>
              </div>
              {recallResult.orders.length === 0 ? (
                <p className="text-sm text-[#6C757D]">No orders contained this product after the batch date.</p>
              ) : (
                <div className="max-h-80 overflow-auto border border-[#E5E5E5] rounded-lg divide-y divide-[#E5E5E5]">
                  {recallResult.orders.map((o) => (
                    <div key={o.order_id} className="p-3 flex justify-between text-sm" data-testid={`recall-order-${o.order_id}`}>
                      <div>
                        <div className="font-semibold text-[#222]">{o.customer_name || "—"}</div>
                        <div className="text-[11px] text-[#6C757D] font-mono">Order {o.order_id?.slice(0, 8)} · {formatDateEU(o.order_date)}</div>
                      </div>
                      <div className="text-[#D62828] font-display font-bold">
                        €{Number(o.subtotal || 0).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <DialogFooter>
                <Button onClick={() => { setRecallTarget(null); setRecallResult(null); }} className="bg-[#1F4D3A] hover:bg-[#173629] text-white" data-testid="recall-close">
                  Close
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print Label Modal */}
      <Dialog open={!!labelTarget} onOpenChange={(o) => !o && setLabelTarget(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="label-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Printer size={20} className="text-[#E85D04]" /> Print DYMO Labels
            </DialogTitle>
          </DialogHeader>
          {labelTarget && (
            <div className="space-y-4">
              {/* Live label preview */}
              <div className="mx-auto rounded border-2 border-dashed border-[#6C757D] p-2 bg-white flex flex-row items-center justify-center gap-2"
                style={{ width: "270px", height: "125px" }}
                data-testid="label-preview"
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/garnacha-logo-round.png`}
                  alt="Garnacha"
                  className="h-[100px] w-[100px] object-contain flex-shrink-0"
                />
                <div className="flex flex-col items-center justify-center flex-1">
                  <div className="font-mono font-extrabold text-[12px] tracking-wide text-[#222]">
                    LOT: {labelTarget.batch_id}
                  </div>
                  <div className="font-mono font-bold text-[13px] text-[#222] mt-1">
                    BB: {bestBeforeFor(labelTarget)}
                  </div>
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-center text-[#6C757D]">
                Preview · 54 × 25 mm · Shelf life {labelTarget.shelf_life_months || 3} month{(labelTarget.shelf_life_months || 3) > 1 ? "s" : ""}
              </div>

              <Field label="Number of labels">
                <Input
                  type="number" min="1" max="200"
                  value={labelCount}
                  onChange={(e) => setLabelCount(e.target.value === "" ? "" : Number(e.target.value))}
                  data-testid="label-count"
                />
              </Field>

              <div className="text-xs text-[#6C757D] bg-[#F7F1E8] rounded p-2">
                <strong>DYMO tip:</strong> in your browser print dialog, pick your DYMO printer and set paper size to <em>54 × 25 mm</em> (or DYMO 11352 / 30336). Margins should be 0.
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setLabelTarget(null)}>Cancel</Button>
                <Button
                  onClick={printLabels}
                  className="bg-[#E85D04] hover:bg-[#c94b03] text-white"
                  data-testid="print-labels-confirm"
                >
                  <Printer size={16} className="mr-2" /> Print {labelCount || 0}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">{label}</Label>
    {children}
  </div>
);
